/**
 * The Premium Hiring status model (developer specification v1.0), mirrored from the backend.
 *
 * A candidate x job record sits on one of ten stages, may carry a detail that belongs to that
 * stage, and when Closed carries a reason. The backend (`ApplicationStatus`, `ClosedReason`,
 * `LegacyApplicationStatus`, `ApplicationWorkflow`) is the authority and refuses anything these
 * lists would not offer; this module exists so a screen offers only what will be accepted.
 *
 * Old stored labels ("Offer sent", "Profile rejected") still resolve, so a row written before
 * the migration shows on the right stage rather than as unknown.
 */

export interface StageInfo {
  label: string;
  /** CRM stage palette suffix, e.g. "lead" -> var(--color-stage-lead-bg). "muted" is special. */
  token: string;
  details: string[];
  /** Used on a self-serve job, where there are no recruiter stages. */
  selfServe: boolean;
}

export const STAGES: StageInfo[] = [
  // Nothing has happened with the candidate yet, so neither reads as progress.
  { label: 'Sourcing', token: 'muted', details: [], selfServe: false },
  { label: 'Contacted', token: 'muted', details: ['Call attempted', 'Message sent', 'Spoke to candidate'], selfServe: false },
  { label: 'Interested', token: 'lead', details: [], selfServe: true },
  { label: 'Screening', token: 'lead', details: ['Screening scheduled', 'Screened'], selfServe: true },
  { label: 'Qualified', token: 'qualified', details: [], selfServe: false },
  { label: 'Submitted', token: 'qualified', details: ['Submitted', 'Resubmitted', 'Under review', 'Feedback pending', 'No feedback'], selfServe: false },
  { label: 'Interview', token: 'proposal', details: ['Interview scheduled', 'Interviewed', 'Assignment / trial', 'Did not show up'], selfServe: true },
  { label: 'Offer', token: 'negotiation', details: ['Shortlisted', 'Offer pending', 'Selected', 'Offer sent', 'Offer accepted'], selfServe: true },
  { label: 'Joined', token: 'won', details: [], selfServe: true },
  { label: 'Closed', token: 'lost', details: [], selfServe: true },
];

export const CLOSED_REASONS: string[] = [
  'Not Interested',
  'No Response',
  'Not Qualified',
  'Compensation Mismatch',
  'Location Issue',
  'Notice Period Issue',
  'Candidate Withdrew',
  'Client Rejected',
  'Interview Rejected',
  'Offer Declined',
  'No Show / Did Not Join',
  'Position Closed',
  'Other',
];

/** The reasons a client may give; the rest describe our side of the process. */
export const EMPLOYER_CLOSED_REASONS: string[] = ['Client Rejected', 'Interview Rejected', 'Position Closed', 'Other'];

/** Stages the employer is never shown on a managed job, and the consent gate sits behind. */
export const BEFORE_SUBMISSION = ['Sourcing', 'Contacted', 'Interested', 'Screening', 'Qualified'];

export const TERMINAL_STAGES = ['Joined', 'Closed'];

const STAGE_BY_KEY = new Map<string, StageInfo>();
for (const s of STAGES) STAGE_BY_KEY.set(s.label.toLowerCase(), s);

interface Mapped {
  stage: string;
  detail?: string;
  closedReason?: string;
  closedNote?: string;
}

/** Old label -> where the migration puts it. Mirrors `LegacyApplicationStatus`. */
const LEGACY = new Map<string, Mapped>();
LEGACY.set('awaiting consent', { stage: 'Qualified' });
for (const d of ['Resubmitted', 'Under review', 'Feedback pending', 'No feedback']) LEGACY.set(d.toLowerCase(), { stage: 'Submitted', detail: d });
for (const d of ['Interview scheduled', 'Interviewed', 'Assignment / trial', 'Did not show up']) LEGACY.set(d.toLowerCase(), { stage: 'Interview', detail: d });
for (const d of ['Shortlisted', 'Offer pending', 'Selected', 'Offer sent', 'Offer accepted']) LEGACY.set(d.toLowerCase(), { stage: 'Offer', detail: d });
LEGACY.set('profile rejected', { stage: 'Closed', closedReason: 'Client Rejected' });
LEGACY.set('interview rejected', { stage: 'Closed', closedReason: 'Interview Rejected' });
LEGACY.set('candidate declined', { stage: 'Closed', closedReason: 'Candidate Withdrew' });
LEGACY.set('duplicate', { stage: 'Closed', closedReason: 'Other', closedNote: 'Duplicate' });
LEGACY.set('role closed', { stage: 'Closed', closedReason: 'Position Closed' });
LEGACY.set('pending', { stage: 'Submitted', detail: 'Submitted' });
LEGACY.set('accepted', { stage: 'Offer', detail: 'Selected' });
LEGACY.set('rejected', { stage: 'Closed', closedReason: 'Client Rejected' });

export interface ResolvedStatus {
  stage: StageInfo | null;
  detail: string;
  closedReason: string;
  closedNote: string;
}

/**
 * Any stored status, with whatever detail and reason the record carries, as stage + detail +
 * reason. A record's own fields win over what an old label implies.
 */
export const resolveStatus = (
  status?: string | null,
  subStatus?: string | null,
  closedReason?: string | null,
): ResolvedStatus => {
  const key = (status ?? '').trim().toLowerCase().replace(/_/g, ' ');
  const direct = STAGE_BY_KEY.get(key);
  const legacy = direct ? undefined : LEGACY.get(key);
  const stage = direct ?? (legacy ? STAGE_BY_KEY.get(legacy.stage.toLowerCase()) ?? null : null);
  return {
    stage,
    detail: subStatus || legacy?.detail || '',
    closedReason: closedReason || legacy?.closedReason || '',
    closedNote: legacy?.closedNote ?? '',
  };
};

/** The stages a job uses: all ten when managed, six when self-serve. */
export const stagesFor = (managed: boolean): StageInfo[] => (managed ? STAGES : STAGES.filter((s) => s.selfServe));

export const detailsFor = (stage?: string | null): string[] => STAGE_BY_KEY.get((stage ?? '').toLowerCase())?.details ?? [];

export const isTerminalStage = (stage?: string | null): boolean => TERMINAL_STAGES.includes(stage ?? '');

/** "Interview · Interviewed", "Closed · Client Rejected", or just the stage. */
export const statusSummary = (
  status?: string | null,
  subStatus?: string | null,
  closedReason?: string | null,
): string => {
  const r = resolveStatus(status, subStatus, closedReason);
  if (!r.stage) return status ?? '';
  const second = r.stage.label === 'Closed' ? r.closedReason : r.detail === r.stage.label ? '' : r.detail;
  return second ? `${r.stage.label} · ${second}` : r.stage.label;
};

export interface StatusSx {
  backgroundColor: string;
  color: string;
}

const tokenSx = (token: string): StatusSx =>
  token === 'muted'
    ? { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' }
    : { backgroundColor: `var(--color-stage-${token}-bg)`, color: `var(--color-stage-${token}-text)` };

// ── The older surface, kept so every screen reads the new model through one door ────────────

interface StageDef {
  stage: string;
  token: string;
  statuses: string[];
}

/** One entry per stage; `statuses` is the stage itself, which is what is stored now. */
export const APPLICATION_STAGES: StageDef[] = STAGES.map((s) => ({ stage: s.label, token: s.token, statuses: [s.label] }));

export const STATUS_OPTIONS: { value: string; label: string }[] = STAGES.map((s) => ({ value: s.label, label: s.label }));

export const STATUS_VALUES: string[] = STATUS_OPTIONS.map((o) => o.value);

/** Stage label for any stored status, old or new, or '' if unknown. */
export const statusToStage = (status?: string | null): string => resolveStatus(status).stage?.label ?? '';

export const statusColor = (status?: string | null): StatusSx => {
  const stage = resolveStatus(status).stage;
  return tokenSx(stage ? stage.token : 'muted');
};

export const stageColor = (stageLabel: string): StatusSx => tokenSx(STAGE_BY_KEY.get(stageLabel.toLowerCase())?.token ?? 'muted');

/** The stage's dot colour, for a stepper or a select value. */
export const stageDotColor = (stageLabel: string): string => {
  const token = STAGE_BY_KEY.get(stageLabel.toLowerCase())?.token ?? 'muted';
  return token === 'muted' ? 'var(--color-text-muted)' : `var(--color-stage-${token})`;
};

/**
 * Chip config for a status, or null when it is not one of ours, so a caller can fall back to
 * its own handling. The chip names the stage; the detail belongs beside it, not inside it.
 */
export const getTaxonomyStatusConfig = (status?: string | null): { label: string; sx: StatusSx } | null => {
  const stage = resolveStatus(status).stage;
  if (!stage) return null;
  return { label: stage.label, sx: tokenSx(stage.token) };
};

/** A job's requirement status. New and Active take candidates; the rest lock the pipeline. */
export const REQUIREMENT_STATUSES = ['New', 'Active', 'On Hold', 'Filled', 'Closed'] as const;
export type RequirementStatus = (typeof REQUIREMENT_STATUSES)[number];

/**
 * The listing flag, which is derived rather than set, and the three values it can hold.
 *
 * EXPIRED is the one that matters here: `getAllJobs` filters it out unless the request names a
 * jobStatus, so it is invisible in the Control Center until something asks for it by name. Kept
 * beside REQUIREMENT_STATUSES because the two are read together and confusing them is the bug:
 * expiry sets requirementStatus to Closed *and* jobStatus to EXPIRED, so reading only the first
 * makes an expired job look like one somebody closed.
 */
export const LISTING_STATES = ['OPEN', 'CLOSED', 'EXPIRED'] as const;

/**
 * Whether a listing includes soft-deleted jobs. Internal only: the backend drops this filter for
 * any caller that is not staff, because getAllJobs is reachable anonymously.
 *
 * "only" and "any" rather than true/false, because "false" could reasonably mean either.
 */
export const DELETED_MODES = ['only', 'any'] as const;

/** A job written before requirement status existed reads its listing flag. */
export const requirementOf = (requirementStatus?: string | null, jobStatus?: string | null): RequirementStatus => {
  const hit = REQUIREMENT_STATUSES.find((r) => r.toLowerCase() === (requirementStatus ?? '').trim().toLowerCase());
  if (hit) return hit;
  const js = (jobStatus ?? '').trim().toUpperCase();
  return js === '' || js === 'OPEN' || js === 'ACTIVE' ? 'Active' : 'Closed';
};

export const requirementColor = (r: string): StatusSx =>
  tokenSx(({ New: 'lead', Active: 'qualified', 'On Hold': 'negotiation', Filled: 'won', Closed: 'muted' } as Record<string, string>)[r] ?? 'muted');

/**
 * Hired, for counting placements and allowing a placement fee: an offer the client has made
 * (Selected, Offer sent, Offer accepted) or Joined. The backend's PlacementFeeService uses the
 * same rule.
 */
export const isHired = (status?: string | null, subStatus?: string | null): boolean => {
  const r = resolveStatus(status, subStatus);
  return r.stage?.label === 'Joined'
    || (r.stage?.label === 'Offer' && ['Selected', 'Offer sent', 'Offer accepted'].includes(r.detail));
};

/**
 * Put to the client: the server's own flag when the record has one, else a stage at or past
 * Submitted (a record from before the flag existed).
 */
export const reachedClient = (app: { status?: string | null; employerVisible?: boolean | null }): boolean => {
  if (app.employerVisible != null) return app.employerVisible;
  const stage = resolveStatus(app.status).stage?.label ?? '';
  return !!stage && stage !== 'Closed' && !BEFORE_SUBMISSION.includes(stage);
};

/** Waiting on the client: submitted, with the feedback detail set. */
export const awaitingFeedback = (status?: string | null, subStatus?: string | null): boolean => {
  const r = resolveStatus(status, subStatus);
  return r.stage?.label === 'Submitted' && ['Feedback pending', 'No feedback'].includes(r.detail);
};
